import { useEffect, useState } from "react";
import { AlertCircle, Building2, Loader2 } from "lucide-react";
import { authFetch } from "../lib/authFetch";
import { useClientAuth } from "../lib/auth-context";
import CompanyProfileSection, { CompanyProfile } from "./societe/CompanyProfileSection";
import DeliveryAddressesSection from "./societe/DeliveryAddressesSection";
import CompanyUsersSection from "./societe/CompanyUsersSection";

export default function SocietePage() {
  const { user, updateCompanyBrand } = useClientAuth();
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    let alive = true;
    authFetch("/api/espace-client/my/company")
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || "Impossible de charger le profil société.");
        return data as CompanyProfile;
      })
      .then(data => {
        if (!alive) return;
        setCompany(data);
        updateCompanyBrand({
          logoUrl: data.logoUrl || null,
          couleurPrimaire: data.couleurPrimaire || "#4e63f0",
          couleurSecondaire: data.couleurSecondaire || "#2facf3",
        });
      })
      .catch(reason => { if (alive) setError(reason.message || "Impossible de charger le profil société."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [updateCompanyBrand]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-64" role="status" aria-label="Chargement du profil"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (error || !company) {
    return <div className="p-6 max-w-5xl mx-auto" role="alert"><div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"><AlertCircle className="w-4 h-4 shrink-0" />{error || "Profil société indisponible."}</div></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <header>
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-primary" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-foreground">Profil de la société</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {isAdmin ? "Consultez et gérez les informations de votre entreprise." : "Informations de votre entreprise en lecture seule."}
        </p>
      </header>

      <CompanyProfileSection company={company} isAdmin={isAdmin} onSaved={setCompany} />
      <DeliveryAddressesSection isAdmin={isAdmin} />
      <CompanyUsersSection isAdmin={isAdmin} />
    </div>
  );
}